import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import usePopoverDismiss from "../../hooks/usePopoverDismiss.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import "./DispatchHeader.css";
import { DispatchContextMenuContext } from "./DispatchContextMenu.jsx";

export const DISPATCH_MENU_ACTION_CLASS_NAME = "dispatch-header-menu-action";
const MENU_ITEM_SELECTOR = ':is([role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]):not(:disabled)';

export function DispatchHeaderMenuSection({ children, label, danger = false }) {
  return (
    <div role="group" aria-label={label} className="dispatch-header-menu-section" data-danger={danger || undefined}>
      <div className="dispatch-header-menu-label">{label}</div>
      {children}
    </div>
  );
}

// Keep children mounted when closed: Railway and confirmations own persistent state.
export default function DispatchHeaderActionMenu({ children, label, contextKey = "", icon: Icon, shouldIgnoreTarget = null, menuClassName = "", iconOnly = false, triggerClassName = "", disabled = false, disabledTitle = "", contextGroup = null, contextOnly = false, triggerBadge = null }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const combined = React.useContext(DispatchContextMenuContext);
  const participates = Boolean(contextGroup && combined);
  const contextOpen = participates && combined.isOpen;
  const [contentContainer] = React.useState(() => typeof document === "undefined" ? null : document.createElement("div"));
  const contentHost = React.useRef(null);
  const triggerRef = React.useRef(null);
  const focusEdgeRef = React.useRef("first");
  const menuId = React.useId();
  const { refs, floatingStyles, isPositioned, update } = useFloating({
    open: isOpen,
    strategy: "fixed",
    placement: "bottom-start",
    middleware: [offset(5), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const close = React.useCallback((restoreFocus = false) => {
    setIsOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);
  React.useEffect(() => {
    if (!participates || !contentContainer) return;
    return combined.register(contextGroup, { container: contentContainer, close, shouldIgnoreTarget });
  }, [participates, combined?.register, contextGroup, contentContainer, close, shouldIgnoreTarget]);
  React.useLayoutEffect(() => {
    if (participates && !contextOpen && contentContainer) contentHost.current?.appendChild(contentContainer);
  }, [participates, contextOpen, contentContainer]);
  React.useEffect(() => { if (participates) combined.close(); }, [participates, combined?.close, contextKey]);
  usePopoverDismiss({
    anchorElement: triggerRef.current,
    floatingRef: refs.floating,
    isOpen,
    onClose: () => close(),
    shouldIgnoreTarget,
  });
  React.useEffect(() => { close(); }, [close, contextKey]);
  React.useEffect(() => { if (disabled) close(); }, [close, disabled]);
  React.useLayoutEffect(() => {
    // The mounted menu can reopen before ResizeObserver notices it was hidden.
    if (isOpen) update();
  }, [isOpen, update]);
  React.useEffect(() => {
    if (!isOpen || !isPositioned) return;
    const items = refs.floating.current?.querySelectorAll(MENU_ITEM_SELECTOR);
    const item = focusEdgeRef.current === "last" ? items?.[items.length - 1] : items?.[0];
    (item || refs.floating.current)?.focus();
  }, [isOpen, isPositioned, refs.floating]);

  const handleKeyDown = (event) => {
    if (!event.currentTarget.contains(event.target)) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    } else if (event.key === "Tab") {
      // Restore the trigger before the browser continues its normal tab order.
      close(true);
    } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      const items = [...event.currentTarget.querySelectorAll(MENU_ITEM_SELECTOR)];
      if (!items.length) return;
      const index = items.indexOf(document.activeElement);
      const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
        : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
      items[next].focus();
    }
  };
  const handleClick = (event) => {
    if (!refs.floating.current?.contains(event.target)) return;
    const action = event.target.closest?.(MENU_ITEM_SELECTOR);
    if (action && !action.disabled && !action.hasAttribute("data-keep-menu-open")) close(true);
  };
  const portalRoot = getReactPortalRoot();
  const content = typeof children === "function" ? children({ isOpen: isOpen || contextOpen }) : children;
  return (
    <>
      {!contextOnly ? <button
        ref={(element) => { triggerRef.current = element; refs.setReference(element); }}
        type="button"
        className={`dispatch-header-menu-trigger ${triggerClassName}`}
        aria-label={iconOnly ? label : undefined}
        disabled={disabled}
        title={disabledTitle || (iconOnly ? label : undefined)}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-expanded={isOpen}
        onClick={() => { focusEdgeRef.current = "first"; setIsOpen(!isOpen); }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            focusEdgeRef.current = event.key === "ArrowUp" ? "last" : "first";
            setIsOpen(true);
          }
        }}
      >
        {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
        {iconOnly ? null : label}
        <ChevronDownIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {triggerBadge}
      </button> : null}
      {portalRoot ? createPortal(
        <div
          ref={refs.setFloating}
          id={menuId}
          role="menu"
          aria-label={label}
          tabIndex={-1}
          hidden={!isOpen}
          className={`dispatch-header-menu ${menuClassName}`}
          style={{ ...floatingStyles, visibility: isOpen && !isPositioned ? "hidden" : undefined }}
          onKeyDownCapture={(event) => {
            if (event.key === "Escape") handleKeyDown(event);
          }}
          onKeyDown={handleKeyDown}
          onClickCapture={handleClick}
        >
          {participates ? <div ref={contentHost} /> : content}
        </div>, portalRoot,
      ) : null}
      {participates && contentContainer ? createPortal(<div
        onKeyDownCapture={(event) => { if (event.key === "Escape") (contextOpen ? combined.handleKeyDown : handleKeyDown)(event); }}
        onKeyDown={contextOpen ? combined.handleKeyDown : handleKeyDown}
        onClickCapture={contextOpen ? combined.handleClick : handleClick}
      >{content}</div>, contentContainer) : null}
    </>
  );
}
